import { useState } from "react";
import { useAuth } from "@/app/context/Auth/AuthContext";
import { useRecommendedJobs, useSavedJobs } from "@/app/services/create-resume/queries.js";
import { ResumeDataService } from "@/app/services/create-resume/resume-data.js";

type InteractionType = "Saved" | "Shared" | "Irrevant";

interface UseJobInteractionsReturn {
	submitting: boolean;
	saveJobInteraction: (interaction: InteractionType) => Promise<void>;
}

/**
 * Hook to manage job interaction actions (save, share, mark irrelevant)
 * @param jobId - The job ID to interact with
 * @param dataId - Optional data ID from props
 * @returns Interaction state and handler
 */
export function useJobInteractions(jobId: string, dataId?: string): UseJobInteractionsReturn {
	const [submitting, setSubmitting] = useState(false);
	const { isLoggedIn: isUserLoggedIn } = useAuth();
	const jobService = new ResumeDataService();
	const { refetch: refetchRecommendedJobs } = useRecommendedJobs();
	const { refetch: refetchSavedJobs } = useSavedJobs();

	const saveJobInteraction = async (interaction: InteractionType) => {
		// The interaction is recorded against the reader's account, so it needs an authenticated
		// user. An anonymous PATCH jobs/:id returns 401, which the global axios interceptor turns
		// into a forced /signin redirect — the reason copying a share link kicked signed-out
		// visitors to sign in. Sharing itself is public, so just skip the record when signed out.
		if (!isUserLoggedIn) return;

		setSubmitting(true);
		await jobService.saveJobInteraction(
			dataId || jobId,
			{ interactionType: interaction },
			() => {
				refetchRecommendedJobs();
				refetchSavedJobs();
				setSubmitting(false);
			},
			() => {
				setSubmitting(false);
			},
		);
	};

	return {
		submitting,
		saveJobInteraction,
	};
}
